import { collection, query, where } from "firebase/firestore";
import { useMemo } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { db } from "@/firebase/app";
import { useCollection } from "@/hooks/firestoreHooks";
import { parseCompetitionInstance, parseCompetitionTemplate } from "@/models/competition";
import { eventDisplays, type LeagueCompetitionEventDisplay } from "@/models/eventDisplay";
import { activeMembership, resolvedLeagueID } from "@/models/user";

export interface ActiveLeagueScope {
  leagueID: string | null;
  organizationID: string | null;
}

export interface LeagueCompetitionsSnapshot {
  leagueID: string | null;
  competitions: LeagueCompetitionEventDisplay[];
  loading: boolean;
  error: string | null;
}

export function useActiveLeagueScope(): ActiveLeagueScope {
  const { currentUser } = useAuth();
  return useMemo(() => {
    if (!currentUser) return { leagueID: null, organizationID: null };
    const context = currentUser.activeContext;
    const membership = activeMembership(currentUser);
    const leagueID =
      context?.leagueID ??
      (context?.targetType === "league" ? context.targetID : undefined) ??
      (membership ? resolvedLeagueID(membership) : undefined) ??
      null;
    const organizationID =
      context?.organizationID ?? (context?.targetType === "organization" ? context.targetID : undefined) ?? null;
    return { leagueID, organizationID };
  }, [currentUser]);
}

export function useLeagueCompetitions(): LeagueCompetitionsSnapshot {
  const { leagueID } = useActiveLeagueScope();
  const instancesQuery = leagueID
    ? query(collection(db, "leagueCompetitionInstances"), where("leagueID", "==", leagueID))
    : null;
  const templatesQuery = leagueID
    ? query(collection(db, "leagueCompetitionTemplates"), where("leagueID", "==", leagueID))
    : null;

  const instances = useCollection(
    instancesQuery,
    parseCompetitionInstance,
    `instances:${leagueID ?? "none"}`,
  );
  const templates = useCollection(
    templatesQuery,
    parseCompetitionTemplate,
    `templates:${leagueID ?? "none"}`,
  );

  return useMemo(
    () => ({
      leagueID,
      competitions: eventDisplays(instances.data, templates.data),
      loading: instances.loading || templates.loading,
      error: instances.error ?? templates.error,
    }),
    [instances, templates, leagueID],
  );
}
